(() => {
  if (window.EmailPopupSectionInitialized) return;
  window.EmailPopupSectionInitialized = true;

  const now = () => Date.now();
  const msFromDays = (days) => Number(days || 0) * 24 * 60 * 60 * 1000;

  document.querySelectorAll('[data-email-popup]').forEach((popup) => {
    const sectionId = popup.dataset.sectionId;
    const dismissedKey = `email-popup-dismissed-${sectionId}`;
    const submittedKey = `email-popup-submitted-${sectionId}`;
    const seenKey = `email-popup-seen-${sectionId}`;
    const delayMs = Number(popup.dataset.delaySeconds || 0) * 1000;
    const reshowMs = msFromDays(popup.dataset.reshowDays);
    const successMs = msFromDays(popup.dataset.successDays);
    const showOncePerVisitor = popup.dataset.showOncePerVisitor === 'true';
    const hideOnMobile = popup.dataset.hideOnMobile === 'true';
    const hideOnDesktop = popup.dataset.hideOnDesktop === 'true';
    const klaviyoEnabled = popup.dataset.klaviyoEnabled === 'true';
    const companyId = popup.dataset.klaviyoCompanyId;
    const revision = popup.dataset.klaviyoRevision || '2024-10-15';
    const successMessage = popup.dataset.successMessage || 'Thanks for subscribing.';
    const errorMessage = popup.dataset.errorMessage || 'We could not subscribe you right now. Please try again.';
    const content = popup.querySelector('[data-email-popup-content]');
    const successView = popup.querySelector('[data-email-popup-success]');
    const successTitle = popup.querySelector('.email-popup__success-title');
    const message = popup.querySelector('.email-popup__message');
    const form = popup.querySelector('.email-popup__form');
    const input = popup.querySelector('.email-popup__input');
    const submit = popup.querySelector('.email-popup__button');
    const syncBodyScrollLock = () => {
      document.body.classList.toggle('email-popup-open', Boolean(document.querySelector('[data-email-popup]:not([hidden])')));
    };
    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    let lastFocused = null;

    const shouldSkipByViewport = () => {
      if (hideOnMobile && window.matchMedia('(max-width: 749px)').matches) return true;
      if (hideOnDesktop && window.matchMedia('(min-width: 750px)').matches) return true;
      return false;
    };

    const getStoredValue = (key) => {
      try {
        return Number(window.localStorage.getItem(key) || 0);
      } catch (error) {
        return 0;
      }
    };

    const setStoredValue = (key, ttl) => {
      try {
        window.localStorage.setItem(key, String(now() + ttl));
      } catch (error) {
        return;
      }
    };

    const setMessage = (state, text) => {
      if (!message) return;
      message.textContent = text;
      message.classList.add('is-visible');
      message.classList.toggle('is-success', state === 'success');
      message.classList.toggle('is-error', state === 'error');
    };

    const clearMessage = () => {
      if (!message) return;
      message.textContent = '';
      message.classList.remove('is-visible', 'is-success', 'is-error');
    };

    const setSuccessState = (text) => {
      if (content) content.classList.add('is-success');
      if (successView) successView.hidden = false;
      if (successTitle) successTitle.textContent = text;
      clearMessage();
    };

    const trapFocus = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = Array.from(popup.querySelectorAll(focusableSelectors)).filter(
        (element) => !element.hasAttribute('hidden') && !element.closest('[hidden]')
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleKeydown = (event) => {
      if (popup.hidden) return;
      if (event.key === 'Escape') {
        closePopup();
        return;
      }

      trapFocus(event);
    };

    const closePopup = ({ persistDismiss = true } = {}) => {
      popup.hidden = true;
      syncBodyScrollLock();
      document.removeEventListener('keydown', handleKeydown);
      if (persistDismiss) setStoredValue(dismissedKey, reshowMs);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };

    const openPopup = () => {
      if (shouldSkipByViewport()) return;
      lastFocused = document.activeElement;
      popup.hidden = false;
      syncBodyScrollLock();
      document.addEventListener('keydown', handleKeydown);
      if (showOncePerVisitor) setStoredValue(seenKey, Number.MAX_SAFE_INTEGER - now());
      window.requestAnimationFrame(() => {
        (input || popup.querySelector('.email-popup__close'))?.focus();
      });
    };

    const shouldAutoOpen = () => {
      if (shouldSkipByViewport()) return false;
      if (showOncePerVisitor && getStoredValue(seenKey) > now()) return false;
      if (getStoredValue(submittedKey) > now()) return false;
      if (getStoredValue(dismissedKey) > now()) return false;
      return true;
    };

    const getScrollTop = () =>
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const hasReachedScrollTrigger = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const scrollTriggerPx = Math.max(64, Math.round(viewportHeight * 0.15));
      return getScrollTop() >= scrollTriggerPx;
    };

    const openWithDelay = () => {
      if (!shouldAutoOpen()) return;

      if (delayMs > 0) {
        window.setTimeout(() => {
          if (shouldAutoOpen()) openPopup();
        }, delayMs);
        return;
      }

      openPopup();
    };

    const queueOpenAfterScroll = () => {
      if (!shouldAutoOpen()) return;

      if (hasReachedScrollTrigger()) {
        openWithDelay();
        return;
      }

      const onScroll = () => {
        if (!hasReachedScrollTrigger()) return;
        window.removeEventListener('scroll', onScroll);
        openWithDelay();
      };

      window.addEventListener('scroll', onScroll, { passive: true });
    };

    syncBodyScrollLock();

    popup.querySelectorAll('[data-email-popup-close]').forEach((button) => {
      button.addEventListener('click', () => closePopup());
    });

    if (message?.dataset.postedSuccess === 'true') {
      const alreadySubmitted = getStoredValue(submittedKey) > now();

      setStoredValue(submittedKey, successMs);
      setStoredValue(dismissedKey, successMs);
      setSuccessState(successMessage);

      if (!alreadySubmitted) {
        openPopup();
      }
    } else if (message?.dataset.hasErrors === 'true') {
      openPopup();
    } else if (shouldAutoOpen()) {
      queueOpenAfterScroll();
    }

    if (!form || !klaviyoEnabled || !companyId) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const email = input?.value.trim();
      if (!email || !submit) return;

      submit.disabled = true;
      clearMessage();

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              revision
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Shopify theme popup',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        subscriptions: {
                          email: {
                            marketing: {
                              consent: 'SUBSCRIBED'
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            })
          }
        );

        if (!response.ok) {
          throw new Error('Klaviyo request failed');
        }

        setStoredValue(submittedKey, successMs);
        setStoredValue(dismissedKey, successMs);
        setSuccessState(successMessage);
      } catch (error) {
        setMessage('error', errorMessage);
      } finally {
        submit.disabled = false;
      }
    });
  });
})();
